<template>
    <div class="gift-card-selector">
        <!-- Gift Cards Grid Section -->
        <section id="products" class="section bg-white py-5">
            <div class="container">
                <div class="row">
                    <div 
                        v-for="(product, index) in products" 
                        :key="product.id"
                        class="col-6 col-md-4 col-lg-2" 
                        :data-aos="'fade-up'" 
                        :data-aos-delay="index * 100"
                    >
                        <div class="gift-card-item">
                            <div class="card h-100 border-0" :class="{ 'selected': selectedCards[product.id]?.quantity > 0 }">
                                <div class="gift-card-image-wrapper">
                                    <img 
                                        loading="lazy" 
                                        :src="product.image_url" 
                                        class="card-img-top gift-card-image"
                                        :alt="product.name"
                                    >
                                    <!-- Quantity Badge -->
                                    <div 
                                        v-if="selectedCards[product.id]?.quantity > 0" 
                                        class="quantity-badge"
                                    >
                                        {{ selectedCards[product.id].quantity }}
                                    </div>
                                </div>
                                
                                <div class="card-title">
                                    <h3 class="h5 text-black text-uppercase gift-card-title">
                                        {{ product.name }}
                                    </h3>
                                </div>

                                <div class="card-body">
                                    <div class="gift-card-price">
                                        {{ formatPrice(product.price) }}€
                                    </div>
                                    
                                    <!-- Quantity Selector -->
                                    <div class="quantity-selector">
                                        <label :for="`qty_${product.id}`" class="form-label">Quantité</label>
                                        <div class="input-group input-group-sm quantity-input-group">
                                            <div class="input-group-prepend">
                                                <button 
                                                    class="btn btn-outline-secondary" 
                                                    type="button" 
                                                    @click="decreaseQuantity(product.id)"
                                                    :disabled="selectedCards[product.id]?.quantity <= 0"
                                                >-</button>
                                            </div>
                                            <input 
                                                type="number" 
                                                class="form-control" 
                                                :id="`qty_${product.id}`" 
                                                :value="selectedCards[product.id]?.quantity || 0" 
                                                min="0"
                                                max="10"
                                                @input="updateQuantity(product.id, $event.target.value)"
                                            >
                                            <div class="input-group-append">
                                                <button 
                                                    class="btn btn-outline-secondary" 
                                                    type="button" 
                                                    @click="increaseQuantity(product.id)"
                                                    :disabled="(selectedCards[product.id]?.quantity || 0) >= 10"
                                                >+</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Add to Cart Section -->
        <section v-if="hasSelectedCards" id="add-to-cart" class="section bg-light py-5">
            <div class="container">
                <div class="row justify-content-center">
                    <div class="col-lg-8">
                        <h2 class="h3 mb-4 text-center section-title">Étape 2 : Validez votre sélection</h2>
                        
                        <!-- Selected Cards Summary -->
                        <div class="mb-4">
                            <div class="card border-primary">
                                <div class="card-header bg-primary text-white">
                                    <h5 class="mb-0">Récapitulatif de votre sélection</h5>
                                </div>
                                <div class="card-body">
                                    <div v-for="(card, productId) in selectedCards" :key="productId" class="summary-item d-flex justify-content-between align-items-center mb-2">
                                        <span>{{ card.name }} x {{ card.quantity }}</span>
                                        <span class="font-weight-bold">{{ formatPrice(card.total) }}€</span>
                                    </div>
                                    <hr class="my-3">
                                    <div class="d-flex justify-content-between align-items-center">
                                        <strong class="h5 mb-0">Total : {{ formatPrice(totalAmount) }}€</strong>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Info Card -->
                        <div class="mb-4">
                            <div class="card border-info">
                                <div class="card-body">
                                    <div class="d-flex align-items-center">
                                        <i class="bi bi-info-circle-fill text-info me-3" style="font-size: 1.5rem;"></i>
                                        <div>
                                            <h6 class="mb-1">Information</h6>
                                            <p class="mb-0 text-muted">Vous pourrez spécifier les bénéficiaires de chaque carte cadeau depuis votre panier.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Submit Button -->
                        <div class="text-center">
                            <button @click="submitOrder" class="btn btn-primary btn-lg" :disabled="isSubmitting">
                                <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                {{ isSubmitting ? 'Ajout en cours...' : 'Ajouter au panier' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup>
import { ref, computed, reactive, watch } from 'vue'
import axios from 'axios'

const props = defineProps({
    products: {
        type: Array,
        required: true
    }
})

// Reactive data
const selectedCards = reactive({})
const isSubmitting = ref(false)

// Computed properties
const hasSelectedCards = computed(() => {
    return Object.keys(selectedCards).length > 0
})

const totalAmount = computed(() => {
    return Object.values(selectedCards).reduce((total, card) => total + card.total, 0)
})

// Methods
const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(price)
}

const updateQuantity = (productId, quantity) => {
    quantity = parseInt(quantity) || 0
    
    if (quantity < 0) quantity = 0
    if (quantity > 10) quantity = 10
    
    const product = props.products.find(p => p.id === productId)
    
    if (quantity > 0) {
        selectedCards[productId] = {
            name: product.name,
            price: product.price,
            quantity: quantity,
            total: product.price * quantity
        }
    } else {
        delete selectedCards[productId]
    }
}

const increaseQuantity = (productId) => {
    const currentQuantity = selectedCards[productId]?.quantity || 0
    if (currentQuantity < 10) {
        updateQuantity(productId, currentQuantity + 1)
    }
}

const decreaseQuantity = (productId) => {
    const currentQuantity = selectedCards[productId]?.quantity || 0
    if (currentQuantity > 0) {
        updateQuantity(productId, currentQuantity - 1)
    }
}

// No need for beneficiary watching anymore

const submitOrder = async () => {
    isSubmitting.value = true
    
    try {
        // Prepare cart items data - simplified without beneficiary logic
        const cartItems = []
        
        Object.entries(selectedCards).forEach(([productId, card]) => {
            // Add each card as a separate cart item
            for (let i = 1; i <= card.quantity; i++) {
                cartItems.push({
                    product_id: parseInt(productId),
                    quantity: 1
                })
            }
        })
        
        // Add items to cart sequentially
        for (const item of cartItems) {
            const response = await axios.post('/api/checkout/cart', item)
            
            if (response.status !== 200) {
                throw new Error('Erreur lors de l\'ajout au panier')
            }
        }
        
        // Show success message
        if (window.app && window.app.$toast) {
            window.app.$toast.success('Cartes cadeaux ajoutées au panier avec succès')
        }
        
        // Redirect to cart
        window.location.href = '/checkout/cart'
        
    } catch (error) {
        console.error('Erreur:', error)
        
        const errorMessage = error.response?.data?.message || error.message || 'Une erreur est survenue lors de l\'ajout au panier'
        
        if (window.app && window.app.$toast) {
            window.app.$toast.error(errorMessage)
        }
    } finally {
        isSubmitting.value = false
    }
}
</script>

<style scoped>
/* Masquer les flèches haut/bas dans les inputs de type number */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

input[type="number"] {
    -moz-appearance: textfield;
    appearance: textfield;
}
</style>
